
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Cookies from "js-cookie";

export default function useContactForm() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });

  axios.defaults.withCredentials = true;
  axios.defaults.baseURL = process.env.REACT_APP_BACKEND_URL;

  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  const fieldRefs = {
    name: useRef(null),
    phone: useRef(null),
    email: useRef(null),
    message: useRef(null),
  };

  // ------------------- VALIDATION -------------------
  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "name":
        if (!value.trim()) error = "Name is compulsory";
        else if (!/^[A-Za-z\s]+$/.test(value.trim()))
          error = "Only text must be entered";
        else if (value.trim().length < 3)
          error = "Name must be minimum of 3 characters";
        else if (value.trim().length > 25)
          error = "Name cannot exceed 25 characters";
        break;

      case "phone":
        if (!value.trim()) error = "Phone is compulsory";
        else if (!/^\d+$/.test(value.trim()))
          error = "Only numbers allowed";
        else if (value.trim().length < 10)
          error = "Number must be minimum 10 digits";
        else if (value.trim().length > 15)
          error = "Number must be maximum 15 digits";
        break;

      case "email":
        if (value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()))
          error = "Invalid email format";
        break;

      case "message":
        if (!value.trim()) error = "Message is compulsory";
        break;
    }

    setValidationErrors((prev) => ({ ...prev, [name]: error }));
    return error === "";
  };

  const validateAll = () => {
    const newErrors = {};
    Object.keys(form).forEach((key) => {
      const isValid = validateField(key, form[key]);
      if (!isValid) newErrors[key] = validationErrors[key];
    });
    setValidationErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.values(newErrors).every((v) => !v);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (touchedFields[name]) validateField(name, value);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouchedFields((prev) => ({ ...prev, [name]: true }));
    validateField(name, value);
  };

  // ------------------- RECAPTCHA SCRIPT LOADER -------------------
  const loadRecaptcha = () => {
    return new Promise((resolve, reject) => {
      if (window.grecaptcha) return resolve(window.grecaptcha);

      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        if (window.grecaptcha) resolve(window.grecaptcha);
        else reject(new Error("Failed to load reCAPTCHA"));
      };
      script.onerror = () => reject(new Error("Failed to load reCAPTCHA"));

      document.body.appendChild(script);
    });
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setFeedback(null);

  if (!validateAll()) {
    const firstInvalidField = Object.keys(validationErrors).find(
      (key) => validationErrors[key]
    );
    if (firstInvalidField && fieldRefs[firstInvalidField]?.current) {
      fieldRefs[firstInvalidField].current.focus();
    }
    return;
  }

  setLoading(true);

  try {
    // Load reCAPTCHA
    const grecaptcha = await loadRecaptcha();
    const recaptchaToken = await new Promise((resolve, reject) => {
      grecaptcha.ready(() => {
        grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, { action: "contact_form_submit" })
          .then(token => token ? resolve(token) : reject("Empty reCAPTCHA token"))
          .catch(reject);
      });
    });

   // 1. Fetch CSRF token from backend before submit
    const csrfRes = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/csrf-token`,
      { withCredentials: true }
    );
    const csrfToken = csrfRes.data.csrfToken;

    // 2. Submit form with token + credentials
    await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/contact`,
      { ...form, recaptchaToken },
      {
        withCredentials: true,
        headers: {
          "X-CSRF-Token": csrfToken,
        },
      }
    );

    console.log("CSRF cookie in browser:", Cookies.get("XSRF-TOKEN"));
    console.log("CSRF token header being sent:", csrfToken);


    setFeedback({
      type: "success",
      message: "Thank you for contacting us. We will get back to you soon.",
    });
    setForm({ name: "", phone: "", email: "", message: "" });
    setValidationErrors({});
    setTouchedFields({});
  } catch (error) {
    console.error("Contact form submission error:", error);
    setFeedback({
      type: "error",
      message:
        error.response?.data?.message ||
        error.message ||
        "Sorry, something went wrong. Please call us anytime using the numbers above.",
    });
  } finally {
    setLoading(false);
  }
};
  
  // ------------------- FEEDBACK TIMER -------------------
  useEffect(() => {
    if (feedback) {
      const timer = setTimeout(() => setFeedback(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  return {
    form,
    loading,
    feedback,
    validationErrors,
    handleChange,
    handleBlur,
    handleSubmit,
    fieldRefs,
  };
}

